import invariant from 'invariant';
import { Immutable, Draft, produce } from 'immer';
import snakeCase from 'lodash/snakeCase';
import {
  LATEST_EMISSIONS_MODEL_SCHEMA_VERSION,
  EmissionsModel,
  InputVariable,
  OutputVariable,
  LocalVariable,
  EmissionsVariable,
  FunctionalUnitNode,
  ProcessNode,
  Edge,
  ProcessVariableEdgeEndpoint,
  EdgeEndpoint,
  EdgeEndpointKind,
  isFunctionalUnitNode,
  isProcessVariableEdgeEndpoint,
  isFunctionalUnitEdgeEndpoint,
  ModelParameter,
  isImportEdgeEndpoint,
  ImportNode,
  ImportEdgeEndpoint,
  ImportedDescendant,
  isImportNode,
  isProcessNode,
  ProcessVariableListKey,
  processVariableListKeys,
  isSocketEdgeEndpoint,
  SocketEdgeEndpoint,
  Socket,
  makeFuSocket,
  ProcessKind,
  ResultVariable,
  ProcessVariable,
  Region,
  RegionAttribute,
  isImportedFunctionalUnitEdgeEndpoint,
  UnboundParameter,
  BaseParameter,
} from './emissionsModelSchema';
import {
  ParameterResolver,
  ModelParameterResolver,
  LATEST_PARAMETER_RESOLVER_SCHEMA_VERSION,
} from './parameterResolverSchema';
import {
  ImportParameterLocation,
  isInputOutputVariableLocation,
  VariableLocation,
  getVariableLocation,
  InputVariableLocation,
  InputVariableFieldLocation,
  OutputVariableLocation,
  OutputVariableFieldLocation,
  EmissionsVariableLocation,
  EmissionsVariableFieldLocation,
  LocalVariableLocation,
  LocalVariableFieldLocation,
  ResultVariableLocation,
  ResultVariableFieldLocation,
} from './emissionsModelLocations';
import { FUNCTIONAL_UNIT_NODE_NAME } from './constants';
import assertNever from '@watershed/shared-util/src/assertNever';
import isEqual from 'lodash/isEqual';
import countBy from 'lodash/countBy';
import { ValidationError } from './validation/validationError';
import { snakeCaseInputMask } from '../utils/stringUtils';
import must from '../utils/must';
import { Graph } from 'graphlib';
import {
  DEFAULT_VARIABLES_FOR_PROCESS_KIND,
  ProcessNodeVariables,
} from './processNodeDefaults';
import { parseExpression } from '../lscript/parser';
import { ASTExpr, stringifyExpr } from '@watershed/lscript/ast';
import {
  ResultKindId,
  RESULT_KIND_IDS,
  ResultKindDefinition,
  getAllResultKindDefinitions,
} from '@watershed/constants/emissionsModel/resultKindDefinitions';
import { SlugParts, parseSlug } from '../lscript/utils';
import { UnexpectedError } from '@watershed/errors/UnexpectedError';
import { joinWithAnd } from '../utils/helpers';
import {
  Unit,
  UnknownUnitError,
  gentlyParseUnitString,
  parseUnitString,
} from '@watershed/lscript/unit';
import { ExprType, makeNullable } from '@watershed/lscript/expr_types';
import pluck from '@watershed/shared-util/src/pluck';
import { emptyExprString } from '../lscript/utils/emptyExpr';
import { gatherReferencesExpr } from '../lscript/utils/gatherReferences';
import { DefaultMap } from '../defaultMap';
import { RawEmissionsModelMatcher } from './emissionsModelMatcherSchema';
import { EmissionsModelPath } from './emissionsModelPaths';
import { BadInputError } from '@watershed/errors/BadInputError';
import {
  EmissionsKindId,
  getAllEmissionsKindDefinitions,
  EmissionsKindDefinition,
} from '@watershed/constants/emissionsModel/emissionsKindDefinitions';
import omit from 'lodash/omit';

type ImmutableModelWithNodes = Immutable<{ nodes: EmissionsModel['nodes'] }>;
type ImmutableModelWithNodesAndEdgesAndSockets = Immutable<{
  nodes: EmissionsModel['nodes'];
  edges: EmissionsModel['edges'];
  sockets: EmissionsModel['sockets'];
}>;
type ImmutableImportedDescendants = Immutable<Array<ImportedDescendant>>;
type DraftModelWithNodes = Draft<{ nodes: EmissionsModel['nodes'] }>;

/**
 * Returns a boolean indicating if a string is the standard name for the
 * functional unit node.
 */
export function isFunctionalUnitNodeName(name: string): boolean {
  return name === FUNCTIONAL_UNIT_NODE_NAME;
}

/**
 * Returns the name of the node.
 */
export function getNodeName(
  node: Immutable<FunctionalUnitNode | ProcessNode>
): string {
  if (isFunctionalUnitNode(node)) {
    return FUNCTIONAL_UNIT_NODE_NAME;
  }
  return node.name;
}

/**
 * Returns all the node names in a model.
 */
export function listNodeNames(model: ImmutableModelWithNodes): Array<string> {
  return [
    FUNCTIONAL_UNIT_NODE_NAME,
    ...model.nodes.processes.map((n) => n.name),
    ...model.nodes.imports.map((n) => n.name),
  ];
}

/**
 * Returns all process and import node names in a model.
 */
export function listProcessImportNodeNames(
  model: ImmutableModelWithNodes
): Array<string> {
  return [
    ...model.nodes.processes.map((n) => n.name),
    ...model.nodes.imports.map((n) => n.name),
  ];
}

/**
 * Given an array of objects with `name` properties, finds the object with the
 * specified name.
 */
export function findByName<T extends { name: string }>(
  list: ReadonlyArray<T>,
  name: string
): T | undefined {
  return list.find((item) => item.name === name);
}

// Returns unique name & display name for a  process node in a given model.
// (Derives initial `name` and `displayName` properties from that. If there are
// already processes of the same kind, adds a numeric suffix to the starter
// `name` and `displayName` values.)
export function createUniqueNameForNewProcessNode(
  kind: ProcessKind,
  existingProcessNodeKinds: Array<ProcessKind>
): { nodeName: string; displayName: string } {
  const processesWithSameKind = existingProcessNodeKinds.filter(
    (k) => k === kind
  );
  let displayName: string = kind;
  if (processesWithSameKind.length !== 0) {
    displayName += ` (${processesWithSameKind.length})`;
  }
  const nodeName = nodeDisplayNameToName(displayName);
  return {
    nodeName,
    displayName,
  };
}

/**
 * Returns the node (of any type) with the specified name.
 */
export function getNode<
  ModelType extends DraftModelWithNodes | ImmutableModelWithNodes,
>(
  model: ModelType,
  nodeName: string
): ProcessNode | ImportNode | FunctionalUnitNode {
  if (isFunctionalUnitNodeName(nodeName)) {
    return model.nodes.functionalUnit;
  }

  const result = findByName(
    [...model.nodes.processes, ...model.nodes.imports],
    nodeName
  );
  invariant(result, `Could not find node "${nodeName}" in emissions model`);
  return result;
}

/**
 * Returns the process node with the specified name.
 */
export function getProcessNode<
  ModelType extends DraftModelWithNodes | ImmutableModelWithNodes,
>(model: ModelType, nodeName: string): ModelType['nodes']['processes'][number] {
  const result = findByName(model.nodes.processes, nodeName);
  invariant(
    result,
    `Could not find process node "${nodeName}" in emissions model`
  );
  return result;
}

/**
 * Returns the import node with the specified name.
 */
export function getImportNode<
  ModelType extends DraftModelWithNodes | ImmutableModelWithNodes,
>(
  model: ModelType,
  name: string
): NonNullable<ModelType['nodes']['imports']>[number] {
  const result = findByName(model.nodes.imports, name);
  invariant(result, `Could not find import node "${name}" in emissions model`);
  return result;
}

/**
 * Get the import nodes with the specified emissions model version ID.
 */
export function getImportNodesByEmvId<
  ModelType extends DraftModelWithNodes | ImmutableModelWithNodes,
>(
  model: ModelType,
  emissionsModelVersionId: string
): NonNullable<ModelType['nodes']['imports']> {
  const importNodes = model.nodes.imports.filter(
    (m) => m.emissionsModelVersionId === emissionsModelVersionId
  );
  invariant(
    importNodes.length > 0,
    `Could not find import nodes having emissions model ` +
      `version Id ${emissionsModelVersionId}`
  );
  return importNodes;
}

/**
 * Returns a list of all the variables in the node with the specified name.
 */
export function listAllVariablesForNodeName(
  model: ImmutableModelWithNodes,
  nodeName: string
): Array<ProcessVariable> {
  const node = getProcessNode(model, nodeName);
  return listAllVariablesForNode(node);
}

/**
 * Returns a list of all the variables in a node.
 */
export function listAllVariablesForNode(
  node: Immutable<ProcessNode>
): Array<ProcessVariable> {
  return [
    ...(node.inputs || []),
    ...(node.outputs || []),
    ...(node.emissions || []),
    ...(node.results || []),
    ...(node.locals || []),
  ];
}

/**
 * Returns an object mapping a VariableKind to the node's array of that variable
 * kind.
 */
export function listVariablesForNodeByKind(
  node: Immutable<ProcessNode>
): Immutable<{
  input: Array<InputVariable>;
  output: Array<OutputVariable>;
  emissions: Array<EmissionsVariable>;
  result: Array<ResultVariable>;
  local: Array<LocalVariable>;
}> {
  return {
    input: node.inputs || [],
    output: node.outputs || [],
    emissions: node.emissions || [],
    result: node.results || [],
    local: node.locals || [],
  };
}

export function listVariableLocationsForNode(
  node: Immutable<ProcessNode>
): Array<VariableLocation> {
  const result: Array<VariableLocation> = [];
  for (const [listKey, variable] of variablesInProcess(node)) {
    result.push(getVariableLocation(node.name, listKey, variable.name));
  }
  return result;
}

type MaybeImmutableProcessNode<
  M extends DraftModelWithNodes | ImmutableModelWithNodes,
> = M['nodes']['processes'][number];

type MaybeImmutableVariable<
  M extends DraftModelWithNodes | ImmutableModelWithNodes,
  K extends ProcessVariableListKey,
> = NonNullable<M['nodes']['processes'][number][K]>[number];

/**
 * Returns an iterable that iterates over each variable in a process node.
 * Yielded values are tuples so you can use the listKey to know the variable
 * type.
 */
export function* variablesInProcess<
  M extends DraftModelWithNodes | ImmutableModelWithNodes,
>(
  process: MaybeImmutableProcessNode<M>
): Iterable<
  | ['inputs', MaybeImmutableVariable<M, 'inputs'>]
  | ['outputs', MaybeImmutableVariable<M, 'outputs'>]
  | ['emissions', MaybeImmutableVariable<M, 'emissions'>]
  | ['locals', MaybeImmutableVariable<M, 'locals'>]
  | ['results', MaybeImmutableVariable<M, 'results'>]
> {
  for (const listKey of processVariableListKeys) {
    switch (listKey) {
      case 'inputs':
        for (const variable of process.inputs ?? []) {
          yield ['inputs', variable];
        }
        break;
      case 'outputs':
        for (const variable of process.outputs ?? []) {
          yield ['outputs', variable];
        }
        break;
      case 'emissions':
        for (const variable of process.emissions ?? []) {
          yield ['emissions', variable];
        }
        break;
      case 'locals':
        for (const variable of process.locals ?? []) {
          yield ['locals', variable];
        }
        break;
      case 'results':
        for (const variable of process.results ?? []) {
          yield ['results', variable];
        }
        break;
      default:
        assertNever(listKey);
    }
  }
}

/**
 * Returns an iterable that iterates over each variable in a model. Yielded
 * values are tuples so you can use the VariableLocation's listKey to know the
 * variable type.
 */
export function* variablesInModel<
  M extends DraftModelWithNodes | ImmutableModelWithNodes,
>(
  em: M
): Iterable<
  | [string, 'inputs', MaybeImmutableVariable<M, 'inputs'>]
  | [string, 'outputs', MaybeImmutableVariable<M, 'outputs'>]
  | [string, 'emissions', MaybeImmutableVariable<M, 'emissions'>]
  | [string, 'locals', MaybeImmutableVariable<M, 'locals'>]
  | [string, 'results', MaybeImmutableVariable<M, 'results'>]
> {
  // Something is wrong, but it should get caught elsewhere
  if (!em || !em.nodes || !em.nodes.processes) return;

  for (const node of em.nodes.processes) {
    for (const info of variablesInProcess<M>(node)) {
      yield [node.name, ...info];
    }
  }
}

/**
 * Returns a set of all the variable names in a node.
 */
export function getNodeVariableNames(
  model: ImmutableModelWithNodes,
  nodeName: string
): Set<string> {
  return new Set(
    listAllVariablesForNodeName(model, nodeName).map((v) => v.name)
  );
}

/**
 * Returns the model parameter with the specified name.
 */
export function getModelParameter<
  T extends
    | Draft<{ parameters: EmissionsModel['parameters'] }>
    | Immutable<{ parameters: EmissionsModel['parameters'] }>,
>(model: T, name: string): T['parameters'][number] {
  const result = findByName(model.parameters, name);
  invariant(result, `Could not find model parameter "${name}"`);
  return result;
}

/**
 * Returns a set of all the model parameter names in a model.
 */
export function getModelParameterNames(
  model: Immutable<{ parameters: EmissionsModel['parameters'] }>
): Set<string> {
  return new Set(model.parameters.map((p) => p.name));
}

/**
 * Returns a list of nodes using a parameter.
 */
export function getNodesUsingParameter(
  model: ImmutableModelWithNodes,
  parameterName: string
): Immutable<Array<ProcessNode | ImportNode>> {
  // TODO (bryan, CALC-303): Write a unit test for
  const regexWordInString = new RegExp(`(^|\\W)${parameterName}($|\\W)`);

  const importNodes = model.nodes.imports.filter((importNode) => {
    return importNode.parameterOverrides.some((po) => {
      return regexWordInString.test(po.expression);
    });
  });

  const processNodes = model.nodes.processes.filter((process) => {
    for (const [listKey, variable] of variablesInProcess(process)) {
      switch (listKey) {
        case 'emissions':
          if (
            doesExpressionUseParameter(
              variable.activityExpression,
              parameterName
            )
          ) {
            return true;
          }
          if (
            doesExpressionUseParameter(variable.emissionsFactor, parameterName)
          ) {
            return true;
          }
          break;
        case 'inputs':
        case 'outputs':
        case 'locals':
        case 'results':
          if (
            variable.expression &&
            doesExpressionUseParameter(variable.expression, parameterName)
          ) {
            return true;
          }
          break;
        default:
          assertNever(listKey);
      }
    }
    return false;
  });

  return [...processNodes, ...importNodes];
}

function doesExpressionUseParameter(
  expression: string,
  parameterName: string
): boolean {
  // TODO: Ideally we could actually parse the expression to know for
  // certain if the parameter is used.
  return new RegExp(`(^|\\W)${parameterName}($|\\W)`).test(expression);
}

/**
 * Gets a variable of any kind from one process node in an emissions model.
 * Loses type information because it's generic.
 */
export function getVariable(
  model: ImmutableModelWithNodes,
  nodeName: string,
  variableName: string
): ProcessVariable {
  const result = findByName(
    listAllVariablesForNodeName(model, nodeName),
    variableName
  );
  invariant(
    result,
    `Could not find variable "${variableName}" in node "${nodeName}"`
  );
  return result;
}

/**
 * Given process node and variable names, get the full VariableLocation
 * (including list key).
 */
export function getFullVariableLocation(
  model: ImmutableModelWithNodes,
  nodeName: string,
  variableName: string
): VariableLocation {
  const node = getProcessNode(model, nodeName);
  for (const listKey of processVariableListKeys) {
    for (const variable of node[listKey] ?? []) {
      if (variable.name === variableName) {
        return getVariableLocation(nodeName, listKey, variableName);
      }
    }
  }
  throw new UnexpectedError(
    `Could not find variable named "${variableName}" in process node "${nodeName}"`
  );
}

// The repetitious code in these getters is there to maintain strict type
// specificity. If we figure out a good way to abstract away the repetition
// while maintaining that specificity, great; but for now we just repeat.
export const getVariableOfType = {
  input<ModelType extends DraftModelWithNodes | ImmutableModelWithNodes>(
    em: ModelType,
    p: InputVariableLocation
  ): NonNullable<ModelType['nodes']['processes'][number]['inputs']>[number] {
    const result = getProcessNode(em, p.nodeName)[p.listKey]?.find(
      (v) => v.name === p.variableName
    );
    UnexpectedError.invariant(
      result,
      `Could not find input variable "${p.variableName}" in node "${p.nodeName}"`
    );
    return result;
  },

  output<ModelType extends DraftModelWithNodes | ImmutableModelWithNodes>(
    em: ModelType,
    p: OutputVariableLocation
  ): NonNullable<ModelType['nodes']['processes'][number]['outputs']>[number] {
    const result = getProcessNode(em, p.nodeName)[p.listKey]?.find(
      (v) => v.name === p.variableName
    );
    UnexpectedError.invariant(
      result,
      `Could not find output variable "${p.variableName}" in node "${p.nodeName}"`
    );
    return result;
  },

  emissions<ModelType extends DraftModelWithNodes | ImmutableModelWithNodes>(
    em: ModelType,
    p: EmissionsVariableLocation
  ): NonNullable<ModelType['nodes']['processes'][number]['emissions']>[number] {
    const result = getProcessNode(em, p.nodeName)[p.listKey]?.find(
      (v) => v.name === p.variableName
    );
    UnexpectedError.invariant(
      result,
      `Could not find emissions variable "${p.variableName}" in node "${p.nodeName}"`
    );
    return result;
  },

  local<ModelType extends DraftModelWithNodes | ImmutableModelWithNodes>(
    em: ModelType,
    p: LocalVariableLocation
  ): NonNullable<ModelType['nodes']['processes'][number]['locals']>[number] {
    const result = getProcessNode(em, p.nodeName)[p.listKey]?.find(
      (v) => v.name === p.variableName
    );
    UnexpectedError.invariant(
      result,
      `Could not find local variable "${p.variableName}" in node "${p.nodeName}"`
    );
    return result;
  },

  result<ModelType extends DraftModelWithNodes | ImmutableModelWithNodes>(
    em: ModelType,
    p: ResultVariableLocation
  ): NonNullable<ModelType['nodes']['processes'][number]['results']>[number] {
    const result = getProcessNode(em, p.nodeName)[p.listKey]?.find(
      (v) => v.name === p.variableName
    );
    UnexpectedError.invariant(
      result,
      `Could not find result variable "${p.variableName}" in node "${p.nodeName}"`
    );
    return result;
  },
} as const;

export const getFieldFromVariableOfType = {
  input<F extends keyof InputVariable>(
    em: ImmutableModelWithNodes,
    p: InputVariableFieldLocation<F>
  ): InputVariable[F] {
    return getVariableOfType.input(em, p.removeField())[p.fieldName];
  },

  output<F extends keyof OutputVariable>(
    em: ImmutableModelWithNodes,
    p: OutputVariableFieldLocation<F>
  ): OutputVariable[F] {
    return getVariableOfType.output(em, p.removeField())[p.fieldName];
  },

  emissions<F extends keyof EmissionsVariable>(
    em: ImmutableModelWithNodes,
    p: EmissionsVariableFieldLocation<F>
  ): EmissionsVariable[F] {
    return getVariableOfType.emissions(em, p.removeField())[p.fieldName];
  },

  local<F extends keyof LocalVariable>(
    em: ImmutableModelWithNodes,
    p: LocalVariableFieldLocation<F>
  ): LocalVariable[F] {
    return getVariableOfType.local(em, p.removeField())[p.fieldName];
  },

  result<F extends keyof ResultVariable>(
    em: ImmutableModelWithNodes,
    p: ResultVariableFieldLocation<F>
  ): ResultVariable[F] {
    return getVariableOfType.result(em, p.removeField())[p.fieldName];
  },
} as const;

/**
 * Returns the edges that connects to a variable (input or output). A valid
 * model can only have one edge per variable, but while creating a model it may
 * be invalid, so we allow for multiple.
 */
export function getEdgesForVariable(
  edges: Immutable<Array<Edge>>,
  variableLocation: VariableLocation
): Array<Edge> {
  if (!isInputOutputVariableLocation(variableLocation)) {
    return [];
  }

  const lookAtTargets = variableLocation.listKey === 'inputs';
  const result = edges.filter((edge) => {
    const comparableEdge = lookAtTargets ? edge.target : edge.source;
    return (
      isProcessVariableEdgeEndpoint(comparableEdge) &&
      comparableEdge.process.name === variableLocation.nodeName &&
      comparableEdge.process.variableName === variableLocation.variableName
    );
  });

  return result;
}

/**
 * Returns the edges that involve the functional unit.
 */
export function getEdgesForFunctionalUnit(
  edges: Immutable<Array<Edge>>
): Array<Edge> {
  return edges.filter((edge) => {
    return (
      isFunctionalUnitEdgeEndpoint(edge.source) ||
      isFunctionalUnitEdgeEndpoint(edge.target)
    );
  });
}

/**
 * Returns the edges that involve the named import node.
 */
export function getEdgesForImportNode(
  edges: Immutable<Array<Edge>>,
  name: string
): Array<Edge> {
  return edges.filter((edge) => {
    return (
      (isImportEdgeEndpoint(edge.source) && edge.source.import.name === name) ||
      (isImportEdgeEndpoint(edge.target) && edge.target.import.name === name)
    );
  });
}

/**
 * Returns the edges that involve the named socket.
 */
export function getEdgesForSocket(
  edges: Immutable<Array<Edge>>,
  socketName: string
): Immutable<Array<Edge>> {
  return edges.filter((edge) => {
    return (
      (isSocketEdgeEndpoint(edge.source) &&
        edge.source.socket.name === socketName) ||
      (isSocketEdgeEndpoint(edge.target) &&
        edge.target.socket.name === socketName)
    );
  });
}

/**
 * Returns the edges that involve any of the variables in the named process
 * node.
 */
export function getEdgesForProcessNode(
  edges: Immutable<Array<Edge>>,
  name: string
): Array<Edge> {
  return edges.filter((edge) => {
    return (
      (isProcessVariableEdgeEndpoint(edge.source) &&
        edge.source.process.name === name) ||
      (isProcessVariableEdgeEndpoint(edge.target) &&
        edge.target.process.name === name)
    );
  });
}

/**
 * Returns the edges that involve the named node and any variables or sockets on
 * it.
 */
export function getEdgesForNode(
  edges: Immutable<Array<Edge>>,
  name: string
): Array<Edge> {
  if (isFunctionalUnitNodeName(name)) {
    return getEdgesForFunctionalUnit(edges);
  }
  // These functions are safe to use for both import and process nodes:
  // getEdgesForImportNode will just return an empty array if it's a process
  // node, etc.
  return [
    ...getEdgesForImportNode(edges, name),
    ...getEdgesForProcessNode(edges, name),
  ];
}

/**
 * Returns the edges for a functional unit node, import node, or process
 * variable.
 */
export function getEdgesForNodeOrVariable(
  model: ImmutableModelWithNodesAndEdgesAndSockets,
  name: string,
  variableLocation?: VariableLocation
): Array<Edge> {
  const node = getNode(model, name);

  if (isFunctionalUnitNode(node)) {
    return getEdgesForFunctionalUnit(model.edges);
  }
  if (isImportNode(node)) {
    return getEdgesForImportNode(model.edges, node.name);
  }
  if (isProcessNode(node)) {
    invariant(
      variableLocation,
      `variableLocation must be provided for process nodes`
    );
    return getEdgesForVariable(model.edges, variableLocation);
  }
  assertNever(node);
}

/**
 * Returns the edge endpoint opposite to the one provided.
 */
export function getOppositeEdgeEndpoint(
  edge: Edge,
  edgeEndpoint: EdgeEndpoint
): EdgeEndpoint {
  if (isEqual(edge.source, edgeEndpoint)) {
    return edge.target;
  }
  if (isEqual(edge.target, edgeEndpoint)) {
    return edge.source;
  }
  throw new UnexpectedError(
    `Edge endpoint "${getEdgeEndpointId(
      edgeEndpoint
    )}" does not exist in edge "${getEdgeId(edge)}"`
  );
}

/**
 * Finds the edges that involve a variable and returns the opposite endpoints.
 */
export function getOppositeEdgeEndpoints(
  edges: Immutable<Array<Edge>>,
  edgeEndpoint: EdgeEndpoint
): Array<EdgeEndpoint> {
  return edges.flatMap((edge) => {
    if (isEqual(edge.source, edgeEndpoint)) {
      return edge.target;
    }
    if (isEqual(edge.target, edgeEndpoint)) {
      return edge.source;
    }
    return [];
  });
}

/**
 * Returns the name of the node that an edge endpoint references.
 */
export function getNodeNameFromEdgeEndpoint(end: EdgeEndpoint): string {
  if (isProcessVariableEdgeEndpoint(end)) {
    return end.process.name;
  } else if (isFunctionalUnitEdgeEndpoint(end)) {
    return FUNCTIONAL_UNIT_NODE_NAME;
  } else if (isImportEdgeEndpoint(end)) {
    return end.import.name;
  } else if (isSocketEdgeEndpoint(end)) {
    return end.socket.name;
  }
  assertNever(end);
}

/**
 * Gets the Unit (with a capital "U") of an edge endpoint.
 */
export function getEdgeEndpointUnit(
  model: Immutable<EmissionsModel>,
  endpoint: EdgeEndpoint,
  importedDescendants: Immutable<Array<ImportedDescendant>>
): Unit | null {
  if (isProcessVariableEdgeEndpoint(endpoint)) {
    // for processes, get the variable's type
    const nonImportVar = getVariable(
      model,
      endpoint.process.name,
      endpoint.process.variableName
    );
    return nonImportVar.unit ? gentlyParseUnitString(nonImportVar.unit) : null;
  }

  if (isImportEdgeEndpoint(endpoint)) {
    const importNode = getImportNode(model, endpoint.import.name);
    const importModel = getImportedDescendantById(
      importedDescendants,
      importNode.emissionsModelVersionId
    );
    const importFunctionalUnitNode = getImportedModelFunctionalUnit(
      model,
      importNode.name,
      importedDescendants
    );
    if (isImportedFunctionalUnitEdgeEndpoint(endpoint)) {
      return gentlyParseUnitString(importFunctionalUnitNode.unit ?? '');
    }
    // recurse into the model and get the socket unit
    return getEdgeEndpointUnit(
      importModel.model,
      { socket: { name: endpoint.import.socketName } },
      importedDescendants
    );
  }

  if (isFunctionalUnitEdgeEndpoint(endpoint)) {
    return model.nodes.functionalUnit.unit
      ? gentlyParseUnitString(model.nodes.functionalUnit.unit)
      : null;
  }

  if (isSocketEdgeEndpoint(endpoint)) {
    const socketUnit = getSocketByName(model, endpoint.socket.name).unit;
    if (socketUnit) {
      return gentlyParseUnitString(socketUnit);
    }
    // if no unit defined, get the unit of the node connected to the socket
    const oppEnd = getOppositeEndOfSocket(model, endpoint.socket.name);
    return getEdgeEndpointUnit(model, oppEnd, importedDescendants);
  }

  throw new UnexpectedError(
    `Unexpected edge endpoint format: ${JSON.stringify(endpoint)}`
  );
}

export function getAllEdgeEndpointsFromImportNode({
  importNode,
  model,
  importedDescendants,
  edgeEndpointKind,
}: {
  importNode: Immutable<ImportNode>;
  model: ImmutableModelWithNodesAndEdgesAndSockets;
  importedDescendants: ImmutableImportedDescendants;
  edgeEndpointKind: EdgeEndpointKind;
}): Map<string, EdgeEndpoint> {
  const result: Map<string, EdgeEndpoint> = new Map();
  const addResult = (candidate: EdgeEndpoint) => {
    result.set(renderEdgeEndpointLabel(model, candidate), candidate);
  };

  addResult({
    import: { name: importNode.name, socketName: FUNCTIONAL_UNIT_NODE_NAME },
  });

  const descendant = getImportedDescendantById(
    importedDescendants,
    importNode.emissionsModelVersionId
  );
  descendant.model.sockets
    .filter((socket) => {
      switch (edgeEndpointKind) {
        case 'target':
          return socket.kind === 'input';
        case 'source':
          return socket.kind === 'output';
        default:
          assertNever(edgeEndpointKind);
      }
    })
    .forEach((socket) => {
      addResult({
        import: { name: importNode.name, socketName: socket.name },
      });
    });

  return result;
}

/**
 * Returns all the edge endpoints that could possibly be connected to the
 * provided one, as a map whose keys are endpoint labels and values are the
 * endpoints.
 */
export function getOppositeEdgeEndpointCandidates(
  model: ImmutableModelWithNodesAndEdgesAndSockets,
  importedDescendants: ImmutableImportedDescendants,
  edgeEndpoint: EdgeEndpoint,
  edgeEndpointKind: EdgeEndpointKind
): Map<string, EdgeEndpoint> {
  const result: Map<string, EdgeEndpoint> = new Map();
  const maybeAddResultEntry = (candidate: EdgeEndpoint) => {
    if (isEqual(edgeEndpoint, candidate)) {
      return;
    }
    result.set(renderEdgeEndpointLabel(model, candidate), candidate);
  };

  for (const processNode of model.nodes.processes) {
    if (edgeEndpointKind !== 'target') {
      for (const inputVariable of processNode.inputs ?? []) {
        maybeAddResultEntry({
          process: { name: processNode.name, variableName: inputVariable.name },
        });
      }
    }
    if (edgeEndpointKind !== 'source') {
      for (const outputVariable of processNode.outputs ?? []) {
        maybeAddResultEntry({
          process: {
            name: processNode.name,
            variableName: outputVariable.name,
          },
        });
      }
    }
  }

  // Add sockets from imported models.
  for (const importNode of model.nodes.imports) {
    const importNodeCandidates = getAllEdgeEndpointsFromImportNode({
      importNode,
      model,
      importedDescendants,
      edgeEndpointKind: edgeEndpointKind === 'target' ? 'source' : 'target',
    });
    importNodeCandidates.forEach((candidate) => {
      maybeAddResultEntry(candidate);
    });
  }

  // Add sockets from the current model.
  model.sockets.forEach((socket) => {
    maybeAddResultEntry({ socket: { name: socket.name } });
  });

  if (!isFunctionalUnitEdgeEndpoint(edgeEndpoint)) {
    maybeAddResultEntry({ functionalUnit: {} });
  }

  return result;
}

/**
 * Returns a string representing an edge endpoint.
 */
export function renderEdgeEndpointLabel(
  model: ImmutableModelWithNodes,
  edgeEndpoint: Immutable<EdgeEndpoint>
): string {
  if (isFunctionalUnitEdgeEndpoint(edgeEndpoint)) {
    return `${model.nodes.functionalUnit.displayName} [functional unit]`;
  }
  if (isProcessVariableEdgeEndpoint(edgeEndpoint)) {
    return `${getProcessNode(model, edgeEndpoint.process.name).displayName}: ${
      edgeEndpoint.process.variableName
    }`;
  }
  if (isImportEdgeEndpoint(edgeEndpoint)) {
    const { name, socketName } = edgeEndpoint.import;
    const socketLabel =
      socketName === FUNCTIONAL_UNIT_NODE_NAME
        ? '[functional unit]'
        : socketName;
    return `${getImportNode(model, name).displayName}: ${socketLabel}`;
  }
  if (isSocketEdgeEndpoint(edgeEndpoint)) {
    return `${edgeEndpoint.socket.name} [socket]`;
  }
  assertNever(edgeEndpoint);
}

/**
 * Returns a boolean indicating if an edge endpoint refers to a variable that
 * has a defined expression.
 */
export function isEdgeEndpointVariableWithExpression(
  model: Immutable<{ nodes: EmissionsModel['nodes']; edges: Array<Edge> }>,
  edgeEndpoint: Immutable<EdgeEndpoint>
): boolean {
  if (!isProcessVariableEdgeEndpoint(edgeEndpoint)) {
    return false;
  }
  const edgeEndpointVariable = getVariable(
    model,
    edgeEndpoint.process.name,
    edgeEndpoint.process.variableName
  );
  return (
    'expression' in edgeEndpointVariable && !!edgeEndpointVariable.expression
  );
}

/**
 * Returns a boolean indicating if a variable is connected (by an edge) to
 * another variable that has a defined expression.
 */
export function isConnectedToVariableWithExpression(
  model: Immutable<{ nodes: EmissionsModel['nodes']; edges: Array<Edge> }>,
  variableLocation: VariableLocation
): boolean {
  if (!isInputOutputVariableLocation(variableLocation)) {
    return false;
  }
  const oppositeEndpoints = getOppositeEdgeEndpoints(model.edges, {
    process: {
      name: variableLocation.nodeName,
      variableName: variableLocation.variableName,
    },
  });
  return oppositeEndpoints.some((end) =>
    isEdgeEndpointVariableWithExpression(model, end)
  );
}

/**
 * Returns a boolean indicating if a variable is connected (by an edge) to the
 * functional unit.
 */
export function isVariableConnectedToFunctionalUnit(
  model: Immutable<{ nodes: EmissionsModel['nodes']; edges: Array<Edge> }>,
  variableLocation: VariableLocation
): boolean {
  if (!isInputOutputVariableLocation(variableLocation)) {
    return false;
  }
  const oppositeEndpoints = getOppositeEdgeEndpoints(model.edges, {
    process: {
      name: variableLocation.nodeName,
      variableName: variableLocation.variableName,
    },
  });
  return oppositeEndpoints.some(isFunctionalUnitEdgeEndpoint);
}

/**
 * Returns a boolean indicating if a variable is connected (by an edge) to an
 * import node.
 */
export function isVariableConnectedToImportNode(
  model: Immutable<{ nodes: EmissionsModel['nodes']; edges: Array<Edge> }>,
  variableLocation: VariableLocation
): boolean {
  if (!isInputOutputVariableLocation(variableLocation)) {
    return false;
  }
  const oppositeEndpoints = getOppositeEdgeEndpoints(model.edges, {
    process: {
      name: variableLocation.nodeName,
      variableName: variableLocation.variableName,
    },
  });
  return oppositeEndpoints.some(isImportEdgeEndpoint);
}

/**
 * Finds all the edges that involve the functional unit and returns the opposite
 * endpoints from those edges.
 */
type NonFunctionalUnitEdgeEndpoint =
  | ProcessVariableEdgeEndpoint
  | ImportEdgeEndpoint
  | SocketEdgeEndpoint;

export function getOppositeEdgeEndpointsForFunctionalUnit(
  edges: Immutable<Array<Edge>>
): {
  source: Array<NonFunctionalUnitEdgeEndpoint>;
  target: Array<NonFunctionalUnitEdgeEndpoint>;
} {
  const edgesWithNode = getEdgesForFunctionalUnit(edges);
  const targets: Array<NonFunctionalUnitEdgeEndpoint> = [];
  const sources: Array<NonFunctionalUnitEdgeEndpoint> = [];
  edgesWithNode.forEach((edge) => {
    if (!isFunctionalUnitEdgeEndpoint(edge.target)) {
      targets.push(edge.target);
      return;
    }
    if (!isFunctionalUnitEdgeEndpoint(edge.source)) {
      sources.push(edge.source);
      return;
    }
    throw new UnexpectedError(
      'Invalid edges: the functional unit is connected to itself'
    );
  });
  return {
    source: sources,
    target: targets,
  };
}

/**
 * Finds all the edges that involve the functional unit and returns the opposite
 * endpoints from those edges.
 */
export function getOppositeEdgeEndpointsByKindForImport(
  edges: Immutable<Array<Edge>>,
  name: string
): { source: Array<EdgeEndpoint>; target: Array<EdgeEndpoint> } {
  const edgesWithNode = getEdgesForImportNode(edges, name);
  const targets: Array<EdgeEndpoint> = [];
  const sources: Array<EdgeEndpoint> = [];
  edgesWithNode.forEach((edge) => {
    if (
      !isImportEdgeEndpoint(edge.target) ||
      edge.target.import.name !== name
    ) {
      targets.push(edge.target);
      return;
    }
    if (
      !isImportEdgeEndpoint(edge.source) ||
      edge.source.import.name !== name
    ) {
      sources.push(edge.source);
      return;
    }
    throw new UnexpectedError(
      `Invalid edges: import node ${name} is connected to itself`
    );
  });
  return {
    source: sources,
    target: targets,
  };
}

/**
 * Gets a deterministic, unique string representation of an edge endpoint.
 */
export function getEdgeEndpointId(
  edgeEndpoint: Immutable<EdgeEndpoint>
): string {
  if (isProcessVariableEdgeEndpoint(edgeEndpoint)) {
    return [edgeEndpoint.process.name, edgeEndpoint.process.variableName].join(
      '.'
    );
  }
  if (isFunctionalUnitEdgeEndpoint(edgeEndpoint)) {
    return FUNCTIONAL_UNIT_NODE_NAME;
  }
  if (isImportEdgeEndpoint(edgeEndpoint)) {
    return `${edgeEndpoint.import.name}:${edgeEndpoint.import.socketName}`;
  }
  if (isSocketEdgeEndpoint(edgeEndpoint)) {
    return edgeEndpoint.socket.name;
  }
  assertNever(edgeEndpoint);
}

/**
 * Gets a deterministic, unique string representation of an edge.
 */
export function getEdgeId(edge: Immutable<Edge>): string {
  return [getEdgeEndpointId(edge.source), getEdgeEndpointId(edge.target)].join(
    '--'
  );
}

/**
 * Returns an array of edges that use the specified node as an endpoint.
 */
export function getEdgesWithNode(
  model: Immutable<{ edges: EmissionsModel['edges'] }>,
  nodeName: string
): Array<Edge> {
  return model.edges.filter((edge) => {
    if (
      isProcessVariableEdgeEndpoint(edge.target) &&
      edge.target.process.name === nodeName
    ) {
      return true;
    }
    if (
      isProcessVariableEdgeEndpoint(edge.source) &&
      edge.source.process.name === nodeName
    ) {
      return true;
    }
    if (
      isImportEdgeEndpoint(edge.target) &&
      edge.target.import.name === nodeName
    ) {
      return true;
    }
    if (
      isImportEdgeEndpoint(edge.source) &&
      edge.source.import.name === nodeName
    ) {
      return true;
    }
    return false;
  });
}

/**
 * Returns a parameter's resolver, or errors if none is found.
 */
export function getModelParameterResolver<
  T extends Immutable<ModelParameterResolver> | ModelParameterResolver,
>(
  parameterResolver: Readonly<{ modelParameters: ReadonlyArray<T> }>,
  parameterName: string
): T {
  for (const resolver of parameterResolver.modelParameters) {
    if (resolver.name === parameterName) {
      return resolver;
    }
  }
  throw new UnexpectedError(
    `Could not find parameter resolver for "${parameterName}"`
  );
}

/**
 * Returns an empty but valid emissions model.
 */
export function createEmptyEmissionsModel({
  functionalUnit = { displayName: 'fu' },
  scope = 'full',
}: {
  functionalUnit?: FunctionalUnitNode;
  scope?: 'full' | 'partial';
} = {}): EmissionsModel {
  return {
    schemaVersion: LATEST_EMISSIONS_MODEL_SCHEMA_VERSION,
    nodes: {
      functionalUnit,
      processes: [],
      imports: [],
    },
    edges: [],
    parameters: [],
    scope,
    sockets: [],
    regions: [],
    unboundParameters: [],
  };
}

/**
 * Creates an empty emissions model matcher.
 */
export function createEmptyMatcher(
  input: Partial<RawEmissionsModelMatcher> &
    Pick<
      RawEmissionsModelMatcher,
      | 'businessActivityType'
      | 'businessActivityTypeId'
      | 'emissionsModelStableId'
    >
): RawEmissionsModelMatcher {
  return {
    filterExpression: null,
    quantitySpecifier: null,
    keepBatVersionAtLatest: false,
    bindings: [],
    kind: 'Draft',
    displayName: null,
    ...input,
  };
}

export function defaultVariablesExistForProcessKind(
  processKind: ProcessKind
): boolean {
  return Object.keys(DEFAULT_VARIABLES_FOR_PROCESS_KIND).includes(processKind);
}

export function getDefaultVariablesForProcessKind(
  processKind: ProcessKind
): ProcessNodeVariables {
  return DEFAULT_VARIABLES_FOR_PROCESS_KIND[processKind] || {};
}

/**
 * Returns a new process node that is valid but filled with empty values.
 */
export function createEmptyProcessNode(
  filler: Partial<ProcessNode> = {}
): ProcessNode {
  return {
    name: '',
    displayName: '',
    inputs: [],
    outputs: [],
    emissions: [],
    locals: [],
    kind: 'Production (generic)',
    ...filler,
  };
}

/**
 * Returns a new import node that is valid but filled with empty values.
 */
export function createEmptyImportNode(
  filler?: Partial<ImportNode>
): ImportNode {
  return {
    name: '',
    displayName: '',
    emissionsModelVersionId: '',
    parameterOverrides: [],
    ...filler,
  };
}

/**
 * Returns a new model parameter that is valid but filled with empty values.
 */
export function createEmptyModelParameter(
  filler?: Partial<ModelParameter>
): ModelParameter {
  return makeModelParameter({
    name: '',
    displayName: '',
    type: 'number',
    ...filler,
  });
}

/**
 * Returns a new parameter resolver that is valid but filled with empty values.
 */
export function createEmptyParameterResolver(
  filler?: Partial<ParameterResolver>
): ParameterResolver {
  return {
    schemaVersion: LATEST_PARAMETER_RESOLVER_SCHEMA_VERSION,
    modelParameters: [],
    ...filler,
  };
}

/**
 * Returns a node `name` derived from a `displayName` value.
 */
export function nodeDisplayNameToName(displayName: string): string {
  return snakeCase(displayName);
}

/**
 * Transforms a string into an acceptable `name` value: lowercase letters,
 * numbers, and underscores.
 */
export function normalizeName(input: string): string {
  return snakeCaseInputMask(input);
}

/**
 * Returns an immutable array of errors for a given path.
 */
export function getErrorsForPath(
  path: EmissionsModelPath,
  errors: Immutable<Array<ValidationError>>
): Immutable<Array<ValidationError>> {
  return errors.filter((error) => {
    return isEqual(error.path.slice(0, path.length), path);
  });
}

/**
 * Returns an immutable array of errors for a given edge.
 */
export function getErrorsForEdge(
  edge: Edge,
  errors: Immutable<Array<ValidationError>>
): Immutable<Array<ValidationError>> {
  const edgeId = getEdgeId(edge);
  return errors.filter((error) => {
    return error.path[1] === 'edges' && error.path[2] === edgeId;
  });
}

/**
 * Returns an edge whose getEdgeId() value matches the provided string. Throws
 * an error if it isn't found.
 */
export function getEdge(
  edges: Immutable<Array<Edge>>,
  edgeId: string
): Immutable<Edge> {
  for (const edge of edges) {
    if (getEdgeId(edge) === edgeId) {
      return edge;
    }
  }
  throw new UnexpectedError(
    `No edge found whose getEdgeId result is "${edgeId}"`
  );
}

/**
 * Returns the edge-related errors for a node or variable.
 */
export function getEdgeErrorsForNodeOrVariable(
  model: ImmutableModelWithNodesAndEdgesAndSockets,
  errors: Immutable<Array<ValidationError>>,
  nodeName: string,
  variableLocation?: VariableLocation
): Immutable<Array<ValidationError>> {
  const node = getNode(model, nodeName);
  if (isProcessNode(node) && !variableLocation) {
    return [];
  }
  const edges = getEdgesForNodeOrVariable(model, nodeName, variableLocation);
  return edges.flatMap((edge) => getErrorsForEdge(edge, errors));
}

/**
 * Returns all the edge-related errors related to a node, including those for
 * variables in a process node.
 */
export function getAllEdgeErrorsForNode(
  model: ImmutableModelWithNodesAndEdgesAndSockets,
  errors: Immutable<Array<ValidationError>>,
  nodeName: string
): Immutable<Array<ValidationError>> {
  const node = getNode(model, nodeName);
  if (isProcessNode(node)) {
    return listVariableLocationsForNode(node)
      .flatMap((variableLocation) =>
        getEdgesForNodeOrVariable(model, node.name, variableLocation)
      )
      .flatMap((edge) => getErrorsForEdge(edge, errors));
  }
  const edges = getEdgesForNodeOrVariable(model, nodeName);
  return edges.flatMap((edge) => getErrorsForEdge(edge, errors));
}

/**
 * Formats an ValidationError into a string
 * Designed for newline-delimited use.
 */
export function formatValidationError(e: Immutable<ValidationError>): string {
  const path = e.path.length > 0 ? e.path.join('.') + ': ' : '';
  return `- ${path}${e.msg}`;
}

/**
 * Returns a comma-separated string representing the path of the current variable,
 * and, optionally, the variable field.
 */
export function createVariableErrorPath(
  variableLocation: VariableLocation,
  variableField?: string
): Array<string> {
  const result = [
    'nodes',
    'processes',
    variableLocation.nodeName,
    variableLocation.listKey,
    variableLocation.variableName,
  ];

  if (variableField) {
    result.push(variableField);
  }

  return result;
}

/**
 * Formats error messages into one string.
 */
export const validationErrorsToString = (arr: Array<ValidationError>): string =>
  arr.map((er) => er.msg).join(' ');

/**
 * Look up an imported model's functional unit
 */
export function getImportedModelFunctionalUnit(
  model: Immutable<EmissionsModel>,
  importNodeName: string,
  importedDescendants: ImmutableImportedDescendants
): FunctionalUnitNode {
  const importedModel = getPrimaryOrImportedEmissionsModel(
    model,
    importNodeName,
    importedDescendants
  );
  return importedModel.nodes.functionalUnit;
}

/**
 * Outputs the model in which `initialNodeName` is found. Includes support for
 * recursively nested models, using the `importedDescendants`. Only includes
 * models reachable from `initialModel`. It will start recursing if the
 * `initialNodeName` has our special `.` separator representing a nesting.
 */
export function getPrimaryOrImportedEmissionsModel(
  initialModel: Immutable<EmissionsModel>,
  initialNodeName: string | null,
  importedDescendants: ImmutableImportedDescendants
): Immutable<EmissionsModel> {
  const nodeNameComponents = initialNodeName?.split('.') ?? [];
  let nodeName = nodeNameComponents.shift();
  let model = initialModel;

  // We can't find the node at all, so just give us back the initial model
  if (!nodeName) {
    return model;
  }

  let node: Immutable<ImportNode> | undefined = undefined;
  while (nodeName) {
    // Look for the node in both the current model's imports, plus all imported descendants's models
    for (const m of [model, ...importedDescendants.map((d) => d.model)]) {
      node = findByName(m.nodes.imports, nodeName);
      if (node !== undefined) {
        break;
      }
    }
    // This happens on editing or deletion of the import node name - the node name might briefly be missing, and that's fine, just return the model.
    if (node === undefined) {
      return model;
    }

    // We now have an EMV id from our new node, so look up that imported descendant and get its model
    const importedDescendant = getImportedDescendantById(
      importedDescendants,
      node.emissionsModelVersionId
    );
    UnexpectedError.invariant(
      importedDescendant,
      `Could not find imported model with id "${node.emissionsModelVersionId}"`,
      { data: { emissionsModelVersionId: node.emissionsModelVersionId } }
    );
    nodeName = nodeNameComponents.shift();
    model = importedDescendant.model;
  }

  return model;
}

/**
 * Returns the expression value of an import parameter override.
 */
export function getImportParameterOverrideExpression(
  model: Immutable<EmissionsModel>,
  importParameterLocation: ImportParameterLocation
): string | undefined {
  const importNode = getImportNode(model, importParameterLocation.nodeName);
  return importNode.parameterOverrides.find(
    (override) => override.name === importParameterLocation.parameterName
  )?.expression;
}

export function getImportedDescendantById(
  importedDescendants: ImmutableImportedDescendants,
  id: string
): Immutable<ImportedDescendant> {
  const descendant = importedDescendants.find((d) => d.id === id);
  UnexpectedError.invariant(
    descendant,
    `Could not find imported model "${id}" in imported descendants`
  );
  return descendant;
}

function getSocketFromDescendantsByName(
  importedDescendants: ImmutableImportedDescendants,
  id: string,
  socketName: string
): Immutable<Socket> {
  const descendant = getImportedDescendantById(importedDescendants, id);
  const socket = descendant.model.sockets.find(
    (sock) => sock.name === socketName
  );
  UnexpectedError.invariant(
    socket,
    `Could not find socket "${socketName}" of "${id}" in imported descendants`
  );
  return socket;
}

/** Given an import endpoint, this function looks up the corresponding import
 * model in the importedDescendants list. */
export function getImportNodeIdForImportEndpoint(
  model: Immutable<EmissionsModel>,
  importEndpoint: ImportEdgeEndpoint
): ImportedDescendant['id'] {
  const importNameToId = new Map(
    model.nodes.imports.map((imp) => [imp.name, imp.emissionsModelVersionId])
  );
  const importName = importEndpoint.import.name;

  const importId = importNameToId.get(importName);
  UnexpectedError.invariant(importId, `Import "${importName}" not found`);
  return importId;
}

export function getSocketFromImportEndpoint(
  model: Immutable<EmissionsModel>,
  importedDescendants: ImmutableImportedDescendants,
  importEndpoint: ImportEdgeEndpoint
): Immutable<Socket> {
  const importId = getImportNodeIdForImportEndpoint(model, importEndpoint);

  const importSocketName = importEndpoint.import.socketName;
  UnexpectedError.invariant(
    importSocketName,
    `Import edge endpoint has no socket, found ${importSocketName}"`
  );

  const socket = getSocketFromDescendantsByName(
    importedDescendants,
    importId,
    importSocketName
  );
  UnexpectedError.invariant(
    socket,
    `Could not find socket "${importSocketName}" of "${importId}" in imported descendants`
  );
  return socket;
}

/**
 * In given model and socket, find it's only edge and return the opposite endpoint
 */
export function getOppositeEndOfSocket(
  model: Immutable<EmissionsModel>,
  socketName: string
): Immutable<EdgeEndpoint> {
  for (const edge of model.edges) {
    const socketEnd = isSocketEdgeEndpoint(edge.source)
      ? edge.source
      : edge.target;
    if (!isSocketEdgeEndpoint(socketEnd)) {
      continue;
    }
    if (socketEnd.socket.name !== socketName) {
      continue;
    }
    const oppEnd = isSocketEdgeEndpoint(edge.source)
      ? edge.target
      : edge.source;
    return oppEnd;
  }
  throw new UnexpectedError(
    `Could not find socket "${socketName}" edge in model`
  );
}

/**
 * Gets a process variable's list key.
 */
export function getVariableListKey(
  model: ImmutableModelWithNodes,
  nodeName: string,
  variableName: string
): ProcessVariableListKey {
  const node = getProcessNode(model, nodeName);
  for (const listKey of processVariableListKeys) {
    for (const variable of node[listKey] ?? []) {
      if (variable.name === variableName) {
        return listKey;
      }
    }
  }
  throw new UnexpectedError(
    `Could not find variable "${variableName}" in node "${nodeName}"`
  );
}

/**
 * Returns the socket with the provided name. throw new EmissionsModelError if it doesn't
 * exist.
 */
export function getSocketByName(
  model: Immutable<EmissionsModel>,
  name: string
): Socket {
  if (name === FUNCTIONAL_UNIT_NODE_NAME) {
    return makeFuSocket();
  }
  const socket = findByName(model.sockets, name);
  UnexpectedError.invariant(
    socket,
    `Could not find socket with name "${name}"`
  );
  return socket;
}

export function getSocketDefinition(
  em: EmissionsModel,
  name: string
): Socket | undefined {
  return em.sockets.find((p: Socket) => p.name === name);
}

// count errors by severity, setting a default of 0
export function countErrorsBySeverity(
  errors: Immutable<Array<ValidationError>>
): { numErrors: number; numWarnings: number } {
  const rawCount = countBy(errors, (e) => e.severity);
  return {
    numErrors: rawCount.error || 0,
    numWarnings: rawCount.warning || 0,
  };
}

interface SerializedImportedDescendant
  extends Pick<
    ImportedDescendant,
    'id' | 'title' | 'description' | 'changelog' | 'createdAt'
  > {
  schemaVersion: string;
  emissionsModelJson: string;
  parameterResolverJson: string;
  stableModel: {
    id: string;
    latestPublishedVersion: {
      id: string;
      changelog: string;
      createdAt: Date;
    } | null;
  };
}

export function parseImportedDescendant(
  d: SerializedImportedDescendant
): ImportedDescendant {
  // latestPublishedVersion can be null if the model has never been published,
  // but that should not be happening in the context of imports, which must be published
  const latestPublishedVersion = must(
    d.stableModel.latestPublishedVersion,
    'imported model must have at least one published version. this should not happen.'
  );
  const importedDescendant = {
    ...d,
    model: JSON.parse(d.emissionsModelJson),
    parameterResolver: JSON.parse(d.parameterResolverJson),
    stableModel: { id: d.stableModel.id, latestPublishedVersion },
  };
  return omit(importedDescendant, [
    'emissionsModelJson',
    'parameterResolverJson',
  ]);
}

/**
 * Use to build a graph for graphlib use.
 * eg usage:
 * model.edges.forEach((edge) => {
    const sourceNode = createGraphNode(edge.source);
    const targetNode = createGraphNode(edge.target);

    if (sourceNode && targetNode) {
      g.setEdge(sourceNode, targetNode);
    }
  });
 */
export const createGraphNode = (
  g: Graph,
  edgeEndpoint: EdgeEndpoint
): string | null => {
  if (isProcessVariableEdgeEndpoint(edgeEndpoint)) {
    g.setNode(edgeEndpoint.process.name);
    return edgeEndpoint.process.name;
  }
  if (isImportEdgeEndpoint(edgeEndpoint)) {
    g.setNode(edgeEndpoint.import.name);
    return edgeEndpoint.import.name;
  }
  if (isFunctionalUnitEdgeEndpoint(edgeEndpoint)) {
    g.setNode(FUNCTIONAL_UNIT_NODE_NAME);
    return FUNCTIONAL_UNIT_NODE_NAME;
  }

  return null;
};

export type SupportedBartValue = string | number | boolean | Date | null;
type SupportedBartValueTypeName =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'null';
export type BartBasedEvaluationValues = {
  orgId: null | string;
  bartRow: Record<
    string,
    {
      value: SupportedBartValue;
      bartColumnType: SupportedBartValueTypeName | '_infer';
    }
  >;
};

export function normalizeDate(date: unknown): Date | null {
  if (date instanceof Date) {
    return date;
  }

  if (typeof date === 'string') {
    return new Date(date);
  }

  if (typeof date === 'number') {
    if (date > 1000 && date <= 9999) {
      return new Date(`${date}-01-01`);
    } else if (date > 9999 && date <= 999999) {
      return new Date(`${date / 100}-${date % 100}-01`);
    } else if (date > 999999 && date <= 99999999) {
      return new Date(`${date / 10000}-${(date % 10000) / 100}-${date % 100}`);
    } else {
      // probably a timestamp
      return new Date(date);
    }
  }
  return null;
}

/*
 * Gets all expressions used currently used including paramaters, parameter overrides, variables
 * Does not look inside import models, beyond overrides (does not access import variables or paramaters)
 */
function getAllExpressionsUsedWithoutImports(
  parameterResolvers: Immutable<ParameterResolver>,
  model: Immutable<EmissionsModel>
): Array<string> {
  return [
    ...expressionsInEmissionsModel(model),
    ...expressionsInParameterResolver(parameterResolvers),
  ];
}

/**
 * Returns an iterable that yields every LScript expression used in an emissions
 * model.
 */
export function* expressionsInEmissionsModel(
  model: Immutable<EmissionsModel>
): Iterable<string> {
  // If you're changing this function, you'll likely also need to change
  // `rewriteExpressionsInEmissionsModel`. For dumb immer reasons (autofreezing
  // the model) the two implementations are hard to unify.

  // Process variable expressions.
  for (const [, listKey, variable] of variablesInModel(model)) {
    if (listKey === 'emissions') {
      yield variable.activityExpression;
      yield variable.emissionsFactor;
    } else if (variable.expression) {
      yield variable.expression;
    }
  }

  // Parameter overrides.
  for (const imp of model.nodes.imports) {
    for (const override of imp.parameterOverrides) {
      yield override.expression;
    }
  }

  // Region attributes.
  function* fromRegions(regions: Immutable<Array<Region>>): Iterable<string> {
    for (const region of regions) {
      if (region.attributes != null) {
        for (const attr of region.attributes) {
          yield attr.expression;
        }
      }
      if (region.subregions) {
        yield* fromRegions(region.subregions);
      }
    }
  }
  yield* fromRegions(model.regions);
}

// Rewrite every expression in an emissions model using the given callback
// function.
export function rewriteExpressionsInEmissionsModel(
  model: Immutable<EmissionsModel>,
  rewrite: (expr: ASTExpr) => ASTExpr
): Immutable<EmissionsModel> {
  // If you're changing this function, you'll likely also need to change
  // `expressionsInEmissionsModel`. For dumb immer reasons (autofreezing the
  // model) the two implementations are hard to unify.

  function rw(expr: string): string {
    return stringifyExpr(rewrite(parseExpression(expr)));
  }
  return produce(model, (draft) => {
    for (const [, listKey, variable] of variablesInModel(draft)) {
      if (listKey === 'emissions') {
        variable.activityExpression = rw(variable.activityExpression);
        variable.emissionsFactor = rw(variable.emissionsFactor);
      } else if (variable.expression) {
        variable.expression = rw(variable.expression);
      }
    }

    // Parameter overrides.
    for (const imp of draft.nodes.imports) {
      for (const param of imp.parameterOverrides) {
        param.expression = rw(param.expression);
      }
    }

    // Region attributes.
    function rewriteRegions(regions: Array<Region>) {
      for (const region of regions) {
        if (region.attributes != null) {
          for (const attr of region.attributes) {
            attr.expression = rw(attr.expression);
          }
        }
        if (region.subregions) {
          rewriteRegions(region.subregions);
        }
      }
    }
    rewriteRegions(draft.regions);
  });
}

/**
 * Returns an iterable that yields every LScript expression used in a parameter
 * resolver.
 */
export function* expressionsInParameterResolver(
  parameterResolver: Immutable<ParameterResolver>
): Iterable<string> {
  for (const param of parameterResolver.modelParameters) {
    yield param.expression;
  }
}

export function rewriteExpressionsInParameterResolver(
  resolver: Immutable<ParameterResolver>,
  rewrite: (expr: ASTExpr) => ASTExpr
): Immutable<ParameterResolver> {
  return produce(resolver, (draft) => {
    for (const param of draft.modelParameters) {
      param.expression = stringifyExpr(
        rewrite(parseExpression(param.expression))
      );
    }
  });
}

export type ImportDisplayNameTree = Map<
  string,
  {
    displayNames: Array<string>;
    tree: ImportDisplayNameTree;
  }
>;

/**
 * Maps from title of node to a list of display names for the
 * nodes that contain this title, plus the recursive tree of the
 * same data for all the imports of that node.
 */
export function getImportDisplayNameTree(
  model: Immutable<EmissionsModel>,
  importedDescendants: ImmutableImportedDescendants
): ImportDisplayNameTree {
  const importTree: ImportDisplayNameTree = new Map();
  for (const imp of model.nodes.imports) {
    const impId = imp.emissionsModelVersionId;
    const impNode = getImportedDescendantById(importedDescendants, impId);
    const impTree = getImportDisplayNameTree(
      impNode.model,
      importedDescendants
    );
    const existingNames = importTree.get(impNode.title)?.displayNames ?? [];
    importTree.set(impNode.title, {
      displayNames: [...existingNames, imp.displayName],
      tree: impTree,
    });
  }
  return importTree;
}

/**
 * Gets all expressions used currently used including paramaters, parameter
 * overrides, variables and imports.
 */
function getAllExpressionsUsedInModel(
  parameterResolvers: Immutable<ParameterResolver>,
  model: Immutable<EmissionsModel>,
  importedDescendants: Immutable<Array<ImportedDescendant>>
): Array<{ expr: string; importModel: string }> {
  const expressionStrings = getAllExpressionsUsedWithoutImports(
    parameterResolvers,
    model
  );
  let output = expressionStrings.map((expr) => {
    return { expr, importModel: '' };
  });
  // Get expressions from through imports
  for (let i = 0; i < importedDescendants.length; i++) {
    const imp = importedDescendants[i];
    const expressionsStringsFromImport = getAllExpressionsUsedWithoutImports(
      imp.parameterResolver,
      imp.model
    );
    output = output.concat(
      expressionsStringsFromImport.map((expr) => {
        return { expr, importModel: imp.title };
      })
    );
  }
  return output;
}

/**
 * Gets all reference data source names used by the model, grouped by import model name.
 */
export function getRefDataUsedByModel(
  parameterResolvers: Immutable<ParameterResolver>,
  model: Immutable<EmissionsModel>,
  importedDescendants: Immutable<Array<ImportedDescendant>>
): Map<string, Set<string>> {
  const expressions = getAllExpressionsUsedInModel(
    parameterResolvers,
    model,
    importedDescendants
  );
  const output = new DefaultMap<string, Set<string>>(() => new Set());
  for (const expression of expressions) {
    const parsedExpr: ASTExpr = parseExpression(expression.expr);
    const slugSet = output.get(expression.importModel);
    for (const ref of gatherReferencesExpr(parsedExpr)) {
      slugSet.add(ref.slug);
    }
  }
  return output.toMap();
}

export function getRefDataSourcesUsedByModelWithoutImports(
  parameterResolver: Immutable<ParameterResolver>,
  model: Immutable<EmissionsModel>
): Array<string> {
  return [
    ...new Set(
      getRefDataVersionNamesUsedByModelWithoutImports(
        parameterResolver,
        model
      ).map((slugPart) => slugPart.referenceDataName)
    ),
  ];
}

export function getRefDataVersionNamesUsedByModelWithoutImports(
  parameterResolver: Immutable<ParameterResolver>,
  model: Immutable<EmissionsModel>
): Array<SlugParts> {
  const expressions = getAllExpressionsUsedWithoutImports(
    parameterResolver,
    model
  );
  const slugs = new Set<string>();
  for (const e of expressions) {
    for (const ref of gatherReferencesExpr(parseExpression(e))) {
      slugs.add(ref.slug);
    }
  }
  return Array.from(slugs).map(parseSlug);
}

/**
 * returns true if the bart parameter field is a JSON attribute
 */
export function isBartFieldJSONAttribute(bartField: string): boolean {
  return bartField.includes('.');
}

// TODO(davidtheclark): Remove after emissions variable migration.
export function getNonEmissionsResultKinds(): Array<ResultKindId> {
  return RESULT_KIND_IDS.filter((k) => !isEmissionsResultKind(k));
}

// TODO(davidtheclark): Remove after emissions variable migration.
export function isEmissionsResultKind(k: ResultKindId): boolean {
  return k.toLowerCase().includes('emissions');
}

/**
 * Returns a variable's display name, taken from the variable itself or the
 * result kind definitions.
 */
export function getVariableDisplayName(variable: ProcessVariable): string {
  if ('resultKind' in variable) {
    const resultKindDef = getResultKindDefinition(variable.resultKind);
    return resultKindDef.displayName;
  }
  return variable.displayName;
}

/**
 * Returns a variable's display name, taken from the variable itself or the
 * result kind definitions.
 */
export function getVariableDescription(
  variable: ProcessVariable
): string | undefined | null {
  if ('resultKind' in variable) {
    const resultKindDef = getResultKindDefinition(variable.resultKind);
    return resultKindDef.description;
  }
  return variable.description;
}

export function renderEmissionsVariableExpression(
  emissionsVariable: Immutable<EmissionsVariable>
): string {
  return `(${emissionsVariable.activityExpression}) * (${emissionsVariable.emissionsFactor})`;
}

/**
 * Returns the model parameter with the specified name, or throws an error if
 * it's not found.
 */
export function getParameter(
  em: EmissionsModel | Immutable<EmissionsModel>,
  name: string
): ModelParameter {
  const result = findByName(em.parameters, name);
  invariant(!!result, `Could not find parameter "${name}" in emissions model`);
  return result;
}

export function getBoundOrUnboundParameter(
  em: EmissionsModel | Immutable<EmissionsModel>,
  name: string
): ModelParameter | UnboundParameter {
  const boundResult = findByName(em.parameters, name);
  if (boundResult) return boundResult;
  try {
    return getUnboundParameter(em, name);
  } catch (e) {
    throw new UnexpectedError('Unable to find bound or unbound parameter', {
      cause: e,
      data: { name },
    });
  }
}

/**
 * Returns the lscript ExprType for a parameter to look for in the model, or throws
 * an error if it's not found. By default, it also throws if we encounter an unparseable unit
 * but that can be suppressed for validation contexts with the use of the options parameter.
 */
export function getExprTypeForParameterWithName(
  em: EmissionsModel | Immutable<EmissionsModel>,
  name: string,
  options: { throwOnUnparseableUnit?: boolean } = {
    throwOnUnparseableUnit: true,
  }
): ExprType {
  const param = getBoundOrUnboundParameter(em, name);
  return getExprTypeForParameter(param, options);
}

/**
 * Returns the lscript ExprType for a specified parameter. By default, it throws if we
 * encounter an unparseable unit but that can be suppressed for validation contexts with
 * the use of the options parameter.
 */
export function getExprTypeForParameter(
  param: Immutable<Pick<ModelParameter, 'nullable' | 'type' | 'unit'>>,
  options: { throwOnUnparseableUnit?: boolean } = {
    throwOnUnparseableUnit: true,
  }
): ExprType {
  let exprType: ExprType = { ty: param.type };
  if (exprType.ty === 'number' && param.unit) {
    try {
      exprType.unit = parseUnitString(param.unit);
    } catch (e) {
      if (options.throwOnUnparseableUnit || !(e instanceof UnknownUnitError)) {
        throw e;
      }
    }
  }
  if (param.nullable) {
    exprType = makeNullable(exprType);
  }
  return exprType;
}

/**
 * Returns the region with the specified name, or throws an error if it's not
 * found.
 */
export function getRegion(em: EmissionsModel, name: string): Region {
  const checkRegion = (region: Region): Region | undefined => {
    if (region.name === name) {
      return region;
    }
    for (const subregion of region.subregions ?? []) {
      const subregionResult = checkRegion(subregion);
      if (subregionResult) {
        return subregionResult;
      }
    }
  };
  for (const region of em.regions) {
    const result = checkRegion(region);
    if (result) {
      return result;
    }
  }
  throw new UnexpectedError(
    `Could not find region "${name}" in emissions model`
  );
}

/**
 * Returns the specified region attribute, or throws an error if it's not found.
 */
export function getRegionAttribute(
  em: EmissionsModel,
  regionName: string,
  attributeName: string
): RegionAttribute {
  const region = getRegion(em, regionName);
  const regionAttributes: Array<RegionAttribute> = region.attributes ?? [];
  const result = findByName(regionAttributes, attributeName);
  invariant(
    !!result,
    `Could not find region attribute "${attributeName}" in region "${regionName}" of emissions model`
  );
  return result;
}

/**
 * Returns an array of regions that apply to the node, ordered by proximity to
 * the node (a.k.a. subbiness): closest -> furthest away. So if the node is in
 * RegionA, which is a subregion of RegionB, which is a subregion of RegionC,
 * the array will be [RegionA, RegionB, RegionC].
 */
export function getEnclosingRegionsInProximityOrder(
  em: EmissionsModel,
  nodeName: string
): Array<Region> {
  // Get the top-level region (in the EmissionsModel, the Region in the
  // regions list, which may or may not have subregions).
  const topLevelCandidates = em.regions.filter((region) =>
    region.nodeNames.includes(nodeName)
  );
  if (topLevelCandidates.length > 1) {
    throw new UnexpectedError(
      `Found a node that is contained in multiple top-level regions, which validation should have blocked: "${nodeName}" is in regions ${joinWithAnd(
        topLevelCandidates.map((r) => `"${r.name}"`)
      )}`,
      { data: { nodeName, emissionsModelRegions: em.regions } }
    );
  }
  const topLevelRegion: Region | undefined = topLevelCandidates[0];

  if (!topLevelRegion) {
    return [];
  }

  const result: Array<Region> = [topLevelRegion];

  const addNestedSubregion = (region: Region): void => {
    const subregionCandidates = (region.subregions ?? []).filter((region) =>
      region.nodeNames.includes(nodeName)
    );
    if (subregionCandidates.length > 1) {
      throw new UnexpectedError(
        `Found a node that is part of multiple subregion branches, which validation should have blocked: "${nodeName}" is in regions ${joinWithAnd(
          subregionCandidates.map((r) => `"${r.name}"`)
        )}, which are both subregions of "${region.name}"`,
        {
          data: {
            regionName: region.name,
            emissionsModelRegions: em.regions,
          },
        }
      );
    }
    const subregion: Region | undefined = subregionCandidates[0];

    if (!subregion) {
      return;
    }

    result.unshift(subregion);
    addNestedSubregion(subregion);
  };
  addNestedSubregion(topLevelRegion);

  return result;
}

export function getUnboundParameter(
  model: Immutable<EmissionsModel>,
  parameterName: string
): UnboundParameter {
  const result = findByName(model.unboundParameters, parameterName);
  UnexpectedError.invariant(
    result,
    `Could not find unbound parameter "${parameterName}" in emissions model`
  );
  return result;
}

/**
 * Returns a set of unbound parameter names present in the model
 */
export function getUnboundParameterNames(
  model: Immutable<{
    unboundParameters: EmissionsModel['unboundParameters'];
  }>
): Set<string> {
  return new Set(model.unboundParameters.map(pluck('name')));
}

/**
 * Returns a set of all parameter names in the model, including both
 * model parameters and unbound parameters.
 */
export function getAllParameterNames(
  model: Immutable<{
    parameters: EmissionsModel['parameters'];
    unboundParameters: EmissionsModel['unboundParameters'];
  }>
): Set<string> {
  return new Set([
    ...getModelParameterNames(model),
    ...getUnboundParameterNames(model),
  ]);
}

export function parameterIsUnbound(
  parameter: Immutable<ModelParameter | UnboundParameter>
): parameter is UnboundParameter {
  return parameter.__emTypename === 'UnboundParameter';
}

interface MakeParameterProps {
  name: BaseParameter['name'];
  type: BaseParameter['type'];
  unit?: BaseParameter['unit'];
  nullable?: BaseParameter['nullable'];
  displayName?: BaseParameter['displayName'];
  description?: BaseParameter['description'];
}

export function makeUnboundParameter(
  props: MakeParameterProps
): UnboundParameter {
  return {
    ...props,
    unit: props.unit ?? null,
    nullable: props.nullable ?? false,
    displayName: props.displayName ?? props.name,
    __emTypename: 'UnboundParameter',
  };
}

export function makeModelParameter(
  props: MakeParameterProps & {
    isEmissionsFactor?: ModelParameter['isEmissionsFactor'];
  }
): ModelParameter {
  return {
    ...props,
    unit: props.unit ?? null,
    nullable: props.nullable ?? false,
    displayName: props.displayName ?? props.name,
    __emTypename: 'ModelParameter',
  };
}

export type UnboundParameterBindingMap = Map<string, string>;

/**
 * Creates a minimal dummy `UnboundParameterBindingMap` that matches the types
 * of the provided `UnboundParameter` definitions. Clearly, this should only be
 * used in cases where you need to pass bindings but really don't care what the
 * values are -- e.g. tests or certain kinds of validation.
 */
export function dummyUnboundParameterBindings(
  unboundParameters: Immutable<Array<UnboundParameter>>
): UnboundParameterBindingMap {
  const result: UnboundParameterBindingMap = new Map();

  for (const p of unboundParameters) {
    result.set(p.name, emptyExprString(getExprTypeForParameter(p)));
  }

  return result;
}

/**
 * Gets the definition of an result kind, throwing an error if the kind
 * doesn't exist.
 */
export function getResultKindDefinition(
  name: ResultKindId
): ResultKindDefinition {
  const allDefs = getAllResultKindDefinitions();
  const def = allDefs.find((d) => d.id === name);
  if (!def) {
    throw new BadInputError(`No result kind named "${name}"`);
  }
  return def;
}

/**
 * Gets the definition of an emissions kind, throwing an error if the kind
 * doesn't exist.
 */
export function getEmissionsKindDefinition(
  name: EmissionsKindId
): EmissionsKindDefinition {
  const allDefs = getAllEmissionsKindDefinitions();
  const def = allDefs.find((d) => d.id === name);
  if (!def) {
    throw new BadInputError(`No emissions kind named "${name}"`);
  }
  return def;
}
